import { apiClient } from '../client';
import type {
  AvailableOperatorsResponse,
  BootstrapResponse,
  DocResponse,
  LandingsListResponse,
  NewsResponse,
  TdsDomain,
  TdsDomainPartner,
  TdsDomainPartnerRequest,
  TdsDomainPartnerSaveResponse,
} from '@/types/api';

export const siteApi = {
  // GET / — bootstrap для кабинета: categories, countries, operators, ...
  bootstrap: () => apiClient.get<BootstrapResponse>('/').then((r) => r.data),

  // Фильтрация и пагинация — серверные (see SiteController::actionLandings).
  // per-page до 1000: потребителям, которым нужен весь список (пикер/опции),
  // достаточно одной «страницы».
  landings: (params: {
    operator_id?: number;
    category_id?: number;
    // Страна берётся у оператора лендинга (LandingQuery::whereCountryId в private).
    country_id?: number;
    subscribe_type_id?: number;
    search?: string;
    page?: number;
    perPage?: number;
    // Контекст ставки для точной цены на форме потока: бэк (Landings::getProfits)
    // подбирает rate-priorities под поток/источник. На витрине не передаются →
    // цена считается без них (см. docs/rate-priorities-resolution.md в private).
    stream_id?: number;
    source_id?: number;
  }) =>
    apiClient
      .get<LandingsListResponse>('/site/landings', {
        params: {
          operator_id: params.operator_id ?? 0,
          category_id: params.category_id ?? 0,
          country_id: params.country_id ?? 0,
          subscribe_type_id: params.subscribe_type_id ?? 0,
          search: params.search ?? '',
          page: params.page ?? 1,
          'per-page': params.perPage ?? 60,
          // Шлём только заданные (>0): пустой source/stream = нет контекста.
          ...(params.stream_id ? { stream_id: params.stream_id } : {}),
          ...(params.source_id ? { source_id: params.source_id } : {}),
        },
      })
      .then((r) => r.data),

  // Операторы, за карточками которых в форме потока есть хоть один лендинг.
  // Справочник операторов (bootstrap.operators) фильтровать нельзя: его же читают
  // фильтры статистики, где нужны все, включая тех, по кому осталась только стата.
  availableOperators: () =>
    apiClient
      .get<AvailableOperatorsResponse>('/site/availableOperators')
      .then((r) => r.data),

  domains: () => apiClient.get<TdsDomain[]>('/site/domains').then((r) => r.data),

  // Свои домены партнёра.
  partnerDomains: {
    list: () =>
      apiClient
        .get<TdsDomainPartner[]>('/site/partnerDomains')
        .then((r) => r.data),

    // Сервер на тот же URL отвечает: пустое тело → возвращает модель,
    // непустое → сохраняет. Используем GET для view, POST для update.
    view: (id: number) =>
      apiClient
        .get<TdsDomainPartner>(`/site/editPartnerDomains/${id}`)
        .then((r) => r.data),

    update: (id: number, payload: TdsDomainPartnerRequest) =>
      apiClient
        .post<TdsDomainPartnerSaveResponse>(
          `/site/editPartnerDomains/${id}`,
          payload,
        )
        .then((r) => r.data),

    create: (payload: TdsDomainPartnerRequest) =>
      apiClient
        .post<TdsDomainPartnerSaveResponse>('/site/addPartnerDomains', payload)
        .then((r) => r.data),

    remove: (id: number) =>
      apiClient
        .delete<number>(`/site/deletePartnerDomains/${id}`)
        .then((r) => r.data),
  },

  // NS-серверы для инструкции по парковке домена (/tds) — значения инстанса из настройки бэка
  // (админка: «Домены партнёров» → «NS-серверы»). Текст инструкции — в i18n кабинета.
  // Пустой список — инструкцию не показываем.
  domainsNs: () =>
    apiClient.get<{ ns: string[] }>('/site/domainsNs').then((r) => r.data.ns),

  // Системные новости с пагинацией (по 5 на страницу серверный дефолт).
  news: (page: number) =>
    apiClient
      .get<NewsResponse>('/site/news', { params: { page } })
      .then((r) => r.data),


  // Документ из таблицы docs (id=1 — Правила; для других id — соглашения,
  // оферты и т.п., используются в модалках согласия на этапе landings/login).
  // Параметр lang-id игнорируется сервером, но AngularJS-клиент его передавал.
  doc: (id: number) =>
    apiClient.get<DocResponse>(`/site/docs/${id}`).then((r) => r.data),

  // Публичный sitekey reCAPTCHA v2 (Yii: SiteController::actionRecaptchatoken).
  // Бэк отдаёт plain string siteKey, не JSON.
  recaptchatoken: () =>
    apiClient.get<string>('/site/recaptchatoken').then((r) => r.data),
};
